import { useState, useEffect } from "react";
import {
  Table,
  Button,
  message,
  Dropdown,
} from "antd";
import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getAllSubmissions, exportSubmissions } from "../services/submissionService";
import dayjs from "dayjs";
import type { MenuProps } from "antd";

interface Submission {
  _id: string;
  projectName: string;
  createdAt: string;
  teamMembers: Array<{ name: string; email: string }>;
  status: string;
  projectDescription: string;
  demoLink: string;
  documentUrl?: string;
  supportingLinks?: Array<{ url: string; description?: string }>;
  leaderEmail?: string;
  leaderName?: string;
  teamName?: string;
}

const Submissions = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch submissions on mount and when pagination changes
  useEffect(() => {
    loadSubmissions();
  }, [pagination.current, pagination.pageSize]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await getAllSubmissions({
        page: pagination.current,
        limit: pagination.pageSize,
      });

      if (response.success) {
        setSubmissions(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.totalItems,
        }));
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to load submissions"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: "csv" | "excel") => {
    try {
      message.loading(`Exporting as ${format.toUpperCase()}...`);
      await exportSubmissions(undefined, format);
      message.success(`Successfully exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      message.error(
        error.response?.data?.message ||
          `Failed to export as ${format.toUpperCase()}`
      );
    }
  };

  const exportMenuItems: MenuProps["items"] = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => handleExport("csv"),
    },
    {
      key: "excel",
      label: "Export as Excel",
      onClick: () => handleExport("excel"),
    },
  ];

  const columns = [
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      render: (text: string) => (
        <span className="text-gray-900 font-medium">
        {text.charAt(0).toUpperCase() + text.slice(1)}
      </span>
      ),
    },
    {
      title: "Date Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (
        <span className="text-sm text-gray-600">
          {dayjs(date).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Submitted By",
      key: "submittedBy",
      render: (_: any, record: Submission) => (
        <span className="text-gray-600">
          {record.leaderName || record.teamMembers[0]?.email || "N/A"}
        </span>
      ),
    },
    {
      title: "",
      key: "action",
      render: (_: any, record: Submission) => (
        <Button
          type="text"
          onClick={() => navigate(`/submissions/${record._id}`)}
          className="text-gray-500 hover:text-primary border border-gray-300 hover:border-primary rounded-md px-3 py-1"
        >
          View details
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Submissions</h1>
        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
          <Button icon={<DownloadOutlined />}>
            Export <DownOutlined />
          </Button>
        </Dropdown>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
        <Table
          columns={columns}
          dataSource={submissions}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: pageSize || 10,
              }));
            },
          }}
          className="submissions-table"
          rowClassName="hover:bg-gray-50"
        />
      </div>
    </div>
  );
};

export default Submissions;
